import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { auth } from '../firebase/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getUserProfile, getUserById, registerUser } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isRegistering, setIsRegistering] = useState(false);

    // Function to fetch user profile from database using authenticated endpoint
    const fetchUserProfile = async (firebaseUser, retryCount = 0) => {
        if (!firebaseUser) {
            setUserProfile(null);
            return;
        }

        try {
            // Get Firebase token and use authenticated endpoint to ensure user is created in DB
            const token = await firebaseUser.getIdToken();
            const profile = await getUserProfile(token);
            setUserProfile(profile);
        } catch (error) {
            console.error('Error fetching user profile:', error);
            
            // If this is a "USER_NOT_REGISTERED" error, handle it
            if (error.code === 'USER_NOT_REGISTERED' || error.message?.includes('User not found in database')) {
                // If registration is in progress, wait and retry (race condition handling)
                if (isRegistering) {
                    console.log('⏳ User registration in progress, waiting and retrying...');
                    // Wait a bit for registration to complete, then retry
                    if (retryCount < 3) {
                        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                        return fetchUserProfile(firebaseUser, retryCount + 1);
                    }
                    return;
                }
                
                // If not registering, try to auto-register (for users who signed up before we had registration)
                // But first, retry a few times in case of race condition
                if (retryCount < 2) {
                    console.log(`🔄 User not found, retrying (attempt ${retryCount + 1}/2)...`);
                    await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                    return fetchUserProfile(firebaseUser, retryCount + 1);
                }
                
                // After retries, try to register
                try {
                    console.log('🔄 User not found in database after retries, attempting to register...');
                    const token = await firebaseUser.getIdToken();

                    // Register user with basic info from Firebase
                    // Use displayName from Firebase Auth if available (it should be set during registration)
                    const userData = {
                        username: firebaseUser.email?.split('@')[0] || firebaseUser.uid.substring(0, 8),
                        display_name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || `User ${firebaseUser.uid.substring(0, 8)}`
                    };

                    await registerUser(userData, token);
                    console.log('✅ User registered successfully, fetching profile...');

                    // Wait a moment for database to commit, then fetch the profile
                    await new Promise(resolve => setTimeout(resolve, 500));
                    const token2 = await firebaseUser.getIdToken();
                    const profile = await getUserProfile(token2);
                    setUserProfile(profile);
                    return;
                } catch (registerError) {
                    console.error('❌ Failed to register user:', registerError);
                    // If it's a USER_ID_CONFLICT, the user already exists, so try to fetch profile again
                    if (registerError.message?.includes('User ID already exists') || 
                        registerError.message?.includes('USER_ID_CONFLICT') ||
                        registerError.message?.includes('already registered')) {
                        console.log('🔄 User already exists, fetching profile...');
                        // Wait a moment for database consistency
                        await new Promise(resolve => setTimeout(resolve, 500));
                        try {
                            const token = await firebaseUser.getIdToken();
                            const profile = await getUserProfile(token);
                            setUserProfile(profile);
                            return;
                        } catch (retryError) {
                            console.error('❌ Failed to fetch profile after conflict:', retryError);
                            setUserProfile(null);
                            return;
                        }
                    }
                    setUserProfile(null);
                    return;
                }
            }
            
            // If still failing after retries or different error, set to null
            setUserProfile(null);
        }
    };

    // Function to refresh user profile (call this after profile updates)
    const refreshUserProfile = async () => {
        if (user) {
            await fetchUserProfile(user);
        }
    };

    // Function to set registration state
    const setRegistrationState = (registering) => {
        setIsRegistering(registering);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await fetchUserProfile(currentUser);
            } else {
                setUserProfile(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const logout = async () => {
        try {
            await signOut(auth);
            setUserProfile(null);
        } catch (error) {
            console.error('Error signing out:', error);
            throw error;
        }
    };

    // Create combined user object with both Firebase and database data
    const combinedUser = useMemo(() => {
        if (!user) return null;
        
        return {
            ...user,
            profile: userProfile,
            displayName: userProfile?.display_name || userProfile?.username || user.displayName || user.email?.split('@')[0],
            // Preserve Firebase user methods
            getIdToken: user.getIdToken.bind(user),
            reload: user.reload ? user.reload.bind(user) : undefined,
            delete: user.delete ? user.delete.bind(user) : undefined
        };
    }, [user, userProfile]);

    const contextValue = useMemo(() => ({
        user: combinedUser, 
        userProfile, 
        loading, 
        logout, 
        refreshUserProfile,
        setRegistrationState
    }), [combinedUser, userProfile, loading, logout, refreshUserProfile, setRegistrationState]);

    return (
        <AuthContext.Provider value={contextValue}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);